import React, {
  useState,
  createContext,
  useContext,
  forwardRef,
  useRef,
} from "react";
import { Autocomplete, TextField, Box } from "@mui/material";

// MUI Autocomplete skips ListboxComponent when options are empty (even with
// freeSolo), so we inject a hidden sentinel to force the listbox to mount.
const EMPTY_LIST_SENTINEL = "\u0000";

const MIN_YEAR = 1900;

const getMaxYear = () => new Date().getFullYear() + 1;

const validateYear = (value, isSpanish) => {
  if (!/^\d{4}$/.test(value)) {
    return isSpanish
      ? "Ingrese un año válido de 4 dígitos"
      : "Enter a valid 4-digit year";
  }
  const year = Number(value);
  const maxYear = getMaxYear();
  if (year < MIN_YEAR || year > maxYear) {
    return isSpanish
      ? `El año debe estar entre ${MIN_YEAR} y ${maxYear}`
      : `Year must be between ${MIN_YEAR} and ${maxYear}`;
  }
  return null;
};

const validateMakeModel = (value, fieldType, isSpanish) => {
  if (!value) {
    if (fieldType === "make") {
      return isSpanish ? "La marca es obligatoria" : "Make is required";
    }
    return isSpanish ? "El modelo es obligatorio" : "Model is required";
  }
  return null;
};

const YmmDropdownContext = createContext(null);

const YmmListbox = forwardRef(function YmmListbox(props, ref) {
  const { children, ...other } = props;
  const ctx = useContext(YmmDropdownContext);
  if (!ctx) {
    return (
      <ul ref={ref} {...other}>
        {children}
      </ul>
    );
  }

  const {
    customEntryText,
    handleCustomEntryChange,
    fieldType,
    label,
    size,
    commitCustomEntry,
    handleCustomEntryBlur,
    customEntryInputRef,
    markCustomEntryInteraction,
  } = ctx;
  const inputMode = fieldType === "year" ? "numeric" : "text";

  const focusCustomEntryInput = () => {
    customEntryInputRef?.current?.focus();
  };

  return (
    <Box
      component="ul"
      ref={ref}
      {...other}
      sx={{
        p: 0,
        m: 0,
        maxHeight: "inherit",
        overflow: "auto",
      }}
    >
      <Box
        component="li"
        role="presentation"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          bgcolor: "background.paper",
          listStyle: "none",
          px: 1,
          pt: 1,
          pb: 0.5,
        }}
        onMouseDown={(e) => {
          markCustomEntryInteraction();
          e.preventDefault();
          focusCustomEntryInput();
        }}
        onTouchStart={(e) => {
          markCustomEntryInteraction();
          e.stopPropagation();
          focusCustomEntryInput();
        }}
      >
        <TextField
          size={size || "medium"}
          fullWidth
          inputRef={customEntryInputRef}
          value={customEntryText}
          placeholder={label}
          onChange={(e) => handleCustomEntryChange(e.target.value)}
          onBlur={handleCustomEntryBlur}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === "Enter") {
              e.preventDefault();
              commitCustomEntry();
            }
          }}
          onMouseDown={(e) => {
            markCustomEntryInteraction();
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            markCustomEntryInteraction();
            e.stopPropagation();
          }}
          inputProps={{ inputMode }}
        />
      </Box>
      {children}
    </Box>
  );
});

const YmmAutocompleteField = ({
  options = [],
  value,
  onChange,
  label,
  disabled = false,
  fieldType = "year",
  paperSx,
  textFieldSx,
  size,
  fullWidth = true,
  onRefetch,
  isSpanish = false,
}) => {
  const [open, setOpen] = useState(false);
  const [customEntryText, setCustomEntryText] = useState("");
  const [error, setError] = useState("");
  const customEntryInputRef = useRef(null);
  const customEntryInteractionRef = useRef(false);
  const skipCustomEntryCommitRef = useRef(false);

  const displayValue = value ?? "";
  const listOptions =
    options.length > 0 ? options : [EMPTY_LIST_SENTINEL];

  const validateCommittedValue = (rawValue) => {
    const committed = rawValue.trim();

    if (fieldType === "year") {
      if (!committed) {
        return { valid: false, empty: true, error: null };
      }
      const yearError = validateYear(committed, isSpanish);
      if (yearError) {
        return { valid: false, error: yearError };
      }
      return { valid: true, value: committed };
    }

    const makeModelError = validateMakeModel(committed, fieldType, isSpanish);
    if (makeModelError) {
      return { valid: false, error: makeModelError };
    }
    return { valid: true, value: committed };
  };

  const markCustomEntryInteraction = () => {
    customEntryInteractionRef.current = true;
  };

  const isFocusMovingToCustomEntryField = (event) => {
    const customEntryInput = customEntryInputRef.current;
    if (!customEntryInput) return false;

    const related = event?.relatedTarget;
    return Boolean(related && customEntryInput.contains(related));
  };

  const handleCustomEntryChange = (nextValue) => {
    setCustomEntryText(nextValue);
    if (error) {
      const result = validateCommittedValue(nextValue);
      if (result.valid) {
        setError("");
      }
    }
  };

  const commitCustomEntry = () => {
    const result = validateCommittedValue(customEntryText);
    if (result.empty) {
      return;
    }
    if (!result.valid) {
      setError(result.error);
      setOpen(true);
      return;
    }

    setError("");
    onChange(result.value);
    if (onRefetch) onRefetch(result.value);
    setCustomEntryText("");
    setOpen(false);
  };

  const handleCustomEntryBlur = (event) => {
    customEntryInteractionRef.current = false;

    const related = event?.relatedTarget;
    const listboxRoot = event?.currentTarget?.closest('[role="listbox"]');
    if (related && listboxRoot?.contains(related)) {
      return;
    }

    setTimeout(() => {
      if (skipCustomEntryCommitRef.current) return;
      commitCustomEntry();
    }, 0);
  };

  const handleOpen = () => {
    if (!disabled) {
      setCustomEntryText("");
      setError("");
      setOpen(true);
    }
  };

  const handleClose = (event, reason) => {
    if (reason === "escape") {
      skipCustomEntryCommitRef.current = true;
    }

    if (reason === "blur") {
      if (isFocusMovingToCustomEntryField(event)) {
        return;
      }
      customEntryInteractionRef.current = false;
    }

    if (reason === "selectOption") {
      setCustomEntryText("");
      setOpen(false);
      return;
    }

    setCustomEntryText("");
    setOpen(false);

    if (reason === "escape") {
      setTimeout(() => {
        skipCustomEntryCommitRef.current = false;
      }, 0);
    }
  };

  const handleChange = (event, newValue) => {
    if (newValue === EMPTY_LIST_SENTINEL) return;
    const committed = String(newValue ?? "");

    if (fieldType === "year") {
      const yearError = validateYear(committed, isSpanish);
      if (yearError) {
        setError(yearError);
        setOpen(true);
        return;
      }
    }

    setError("");
    onChange(committed);
    if (onRefetch && committed) {
      onRefetch(committed);
    }
  };

  const openPicker = (e) => {
    if (!disabled) {
      e.preventDefault();
      setCustomEntryText("");
      setError("");
      setOpen(true);
    }
  };

  const dropdownContext = {
    customEntryText,
    handleCustomEntryChange,
    fieldType,
    label,
    size,
    commitCustomEntry,
    handleCustomEntryBlur,
    customEntryInputRef,
    markCustomEntryInteraction,
  };

  return (
    <YmmDropdownContext.Provider value={dropdownContext}>
      <Autocomplete
        freeSolo
        fullWidth={fullWidth}
        size={size}
        options={listOptions}
        value={displayValue || null}
        disabled={disabled}
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        onChange={handleChange}
        inputValue={displayValue}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === "clear") {
            onChange("");
            setCustomEntryText("");
            setError("");
          }
        }}
        openOnFocus={false}
        autoSelect={false}
        forcePopupIcon
        filterOptions={(opts) =>
          options.length > 0
            ? opts.filter((o) => o !== EMPTY_LIST_SENTINEL)
            : [EMPTY_LIST_SENTINEL]
        }
        getOptionLabel={(option) =>
          option === EMPTY_LIST_SENTINEL ? "" : String(option ?? "")
        }
        isOptionEqualToValue={(option, val) =>
          option === EMPTY_LIST_SENTINEL ? false : option === val
        }
        getOptionDisabled={(option) => option === EMPTY_LIST_SENTINEL}
        ListboxComponent={YmmListbox}
        noOptionsText=""
        slotProps={{ paper: { sx: paperSx } }}
        renderOption={(props, option) => {
          if (option === EMPTY_LIST_SENTINEL) {
            return (
              <li
                {...props}
                key="ymm-empty-sentinel"
                aria-hidden="true"
                style={{ display: "none" }}
              />
            );
          }
          return <li {...props}>{option}</li>;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            sx={textFieldSx}
            error={Boolean(error)}
            helperText={error}
            inputProps={{
              ...params.inputProps,
              readOnly: true,
              inputMode: "none",
              onMouseDown: openPicker,
              onTouchStart: openPicker,
            }}
          />
        )}
      />
    </YmmDropdownContext.Provider>
  );
};

export default YmmAutocompleteField;
